import * as Accordion from "#root/components/accordion";
import { Case, Switch } from "#root/components/conditionals";
import * as Fields from "#root/components/form/fields";
import { GlyphLoading } from "#root/components/glyphs";
import Sidebar from "#root/components/sidebar";
import StandardEditor from "#root/components/standard-editor";
import { useData } from "#root/utility/hooks";
import { mergeUpdate } from "#root/utility/merge-update";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import { flatToNamedTree } from "#root/utility/tree";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getNewsRPMInstanceDestinationCollection, type NewsRPMInstanceDestinationListItem } from "#root/schema/platform/newsrpm-instance";
import PageLayout from "./layout";

export default function ({ instanceId, destinationId }: { instanceId: string; destinationId: string }): ReactNode {
  const navigate = useNavigate();
  const destinationCollection = useMemo(() => getNewsRPMInstanceDestinationCollection(instanceId), [instanceId]);

  const [destinations, setDestinations] = useData(async () => {
    const { rows } = await destinationCollection.list();
    return rows;
  }, [destinationCollection]);

  const handleUpdate = useCallback(
    (data: RecordModificationResponse<NewsRPMInstanceDestinationListItem>) => {
      setDestinations(mergeUpdate(destinations!, data));

      if ("deleted" in data) {
        navigate(`/admin/platform/newsrpm-instance/${instanceId}/destination`);
      } else if ("created" in data) {
        navigate(`/admin/platform/newsrpm-instance/${instanceId}/destination/${data.created}`);
      }
    },
    [navigate, setDestinations, instanceId, destinations],
  );

  if (!destinations) {
    return (
      <PageLayout title="Destination">
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Admin - Destination">
      <DestinationSidebar instanceId={instanceId} destinations={destinations} destinationId={destinationId}/>
      <section>
        <Switch>
          <Case expr={destinationId === "new"}>
            <h1>New Destination</h1>
            <DestinationEditor collection={destinationCollection} onSave={handleUpdate}/>
          </Case>
          <Case expr={destinationId != null}>
            <h1>Edit Destination</h1>
            <DestinationEditor id={destinationId} collection={destinationCollection} onSave={handleUpdate} onDelete={handleUpdate}/>
          </Case>
        </Switch>
      </section>
    </PageLayout>
  );
}

function DestinationSidebar({
                              instanceId,
                              destinations,
                              destinationId,
                            }: {
  instanceId: string;
  destinations: NewsRPMInstanceDestinationListItem[];
  destinationId: string;
}) {
  const navigate = useNavigate();
  const categories = useMemo(() => flatToNamedTree(destinations, d => d.type, d => d.name), [destinations]);

  return (
    <Sidebar>
      <button className="flat center" type="button" onClick={() => navigate(`/admin/platform/newsrpm-instance/${instanceId}/destination/new`)}>
        New Destination
      </button>
      <Accordion.Parent initialItemId={destinationId}>
        {categories.map(({ name, items }) => (
          <Accordion.Group key={name} label={`Type: ${name}`}>
            {items.map(item => (
              <Accordion.Item key={item.id} itemId={item.id}>
                <Link to={`/admin/platform/newsrpm-instance/${instanceId}/destination/${item.id}`}>{item.name}</Link>
              </Accordion.Item>
            ))}
          </Accordion.Group>
        ))}
      </Accordion.Parent>
    </Sidebar>
  );
}

function DestinationEditor(props) {
  return (
    <StandardEditor {...props}>
      <Fields.Set name="name">
        <label>Destination Name</label>
        <Fields.String/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="type">
        <label>Destination Tye</label>
        <Fields.String/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="filter">
        <label>Filter</label>
        <Fields.JSONEditor rows={10}/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="options">
        <label>Transmission Options</label>
        <Fields.JSONEditor rows={10}/>
        <Fields.Validity/>
      </Fields.Set>
    </StandardEditor>
  );
}
